import { useEffect, useState, type ReactNode } from 'react'
import {
  useQueries,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import type { KeyDocumentRequirementDTO } from '@deepguide-ai/types'
import { Badge, dotVariants } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { FileText, ListFilter, Pencil, Search, Sparkles, Upload, Workflow } from 'lucide-react'
import { DocumentRelatedTest } from './DocumentRelatedTest'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { VAULT_STATUS_LABELS } from '@/lib/vault-document.constants'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { useAskAi } from '@/components/chat/useAskAi'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { OrgChart } from '@/components/compliance/people/OrgChart'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { UploadDocumentDialog } from './UploadDocumentDialog'
import {
  VaultDocumentCategoryBadge,
  VaultDocumentSourceIcon,
  VaultDocumentStatusBadge,
} from './VaultDocumentMetadata'

function preparationStatus(entry: KeyDocumentRequirementDTO) {
  if (entry.preparation === 'prepared')
    return { label: 'Ready', status: 'green' as const }
  if (entry.preparation === 'needs_review')
    return { label: 'Review changes', status: 'amber' as const }
  // Legacy Test links are reuse candidates, not a new Key Document draft.
  if ((entry.deliverableKey && entry.preparation === 'draft') || entry.documents.some((d) => d.isSelected && d.status === 'ACTIVE'))
    return { label: 'To review', status: 'blue' as const }
  return { label: 'To prepare', status: 'default' as const }
}

const PREPARATION_LABELS = ['To prepare', 'To review', 'Review changes', 'Ready']

export function KeyDocumentPreparationBadge({
  entry,
}: {
  entry: KeyDocumentRequirementDTO
}) {
  const status = preparationStatus(entry)
  return <Badge status={status.status}>{status.label}</Badge>
}

export function KeyDocumentsPanel({ workspaceId }: { workspaceId: string }) {
  const query = useQuery(trpc.vaultDocument.keyDocuments.queryOptions())
  const [filter, setFilter] = useState<'all' | 'required' | 'optional'>('all')
  const [preparation, setPreparation] = useState('all')
  const [status, setStatus] = useState('all')
  const [framework, setFramework] = useState('all')
  const [search, setSearch] = useState('')
  const docs = query.data ?? []
  const required = docs.filter((d) => d.requirement === 'required')
  const optional = docs.filter((d) => d.requirement === 'optional')
  const hasStatus = (d: KeyDocumentRequirementDTO, value: string) =>
    d.documents.some((document) => document.status === value)
  const frameworks = [...new Set(docs.flatMap((d) => d.frameworks ?? []))].sort()
  const visible = (
    filter === 'all' ? docs : filter === 'optional' ? optional : required
  ).filter(
    (d) =>
      d.title.toLowerCase().includes(search.toLowerCase()) &&
      (preparation === 'all' || preparationStatus(d).label === preparation) &&
      (status === 'all' || hasStatus(d, status)) &&
      (framework === 'all' || !!d.frameworks?.includes(framework))
  )
  if (query.isPending)
    return (
      <div
        className='space-y-5'
        aria-label='Loading key documents'
        aria-busy='true'
      >
        <div>
          <Skeleton className='h-4 w-48 rounded-sm' />
        </div>

        <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
          <Skeleton className='h-9 w-full rounded-sm sm:w-80' />
          <Skeleton className='h-9 w-full rounded-sm sm:w-40' />
          <Skeleton className='h-9 w-full rounded-sm sm:w-40' />
          <Skeleton className='h-9 w-full rounded-sm sm:w-32' />
          <Skeleton className='h-9 w-full rounded-sm sm:w-36' />
        </div>

        <div className='border-border min-w-0 overflow-hidden rounded-sm border bg-card'>
          <div className='border-border bg-surface-subtle grid h-10 min-w-[1040px] grid-cols-[minmax(260px,1fr)_120px_110px_80px_90px_120px_130px_56px] items-center border-b'>
            <div className='px-3'><Skeleton className='h-3 w-28 rounded-sm' /></div>
            <div className='px-3'><Skeleton className='h-3 w-12 rounded-sm' /></div>
            <div className='px-3'><Skeleton className='h-3 w-16 rounded-sm' /></div>
            <div className='px-3'><Skeleton className='h-3 w-12 rounded-sm' /></div>
            <div className='px-3'><Skeleton className='h-3 w-12 rounded-sm' /></div>
            <div className='px-3'><Skeleton className='h-3 w-20 rounded-sm' /></div>
            <div className='px-3'><Skeleton className='h-3 w-20 rounded-sm' /></div>
            <div className='px-3'><Skeleton className='size-6 rounded-sm' /></div>
          </div>
          <div className='bg-surface-subtle flex h-8 min-w-[1040px] items-center px-3'>
            <Skeleton className='h-3 w-24 rounded-sm' />
          </div>
          {Array.from({ length: 7 }, (_, index) => (
            <div
              key={index}
              data-testid='key-document-skeleton-row'
              className='border-border grid min-h-[58px] min-w-[1040px] grid-cols-[minmax(260px,1fr)_120px_110px_80px_90px_120px_130px_44px] items-center border-b last:border-b-0'
            >
              <div className='space-y-2 px-3 py-2.5'>
                <div className='flex items-center gap-2'>
                  <Skeleton className={`h-4 rounded-sm ${index % 3 === 0 ? 'w-44' : index % 3 === 1 ? 'w-52' : 'w-36'}`} />
                  <Skeleton className='h-5 w-12 rounded-sm' />
                </div>
                <Skeleton className={`h-3 rounded-sm ${index % 2 === 0 ? 'w-3/4' : 'w-2/3'}`} />
              </div>
              <div className='px-3'><Skeleton className='h-5 w-20 rounded-sm' /></div>
              <div className='px-3'><Skeleton className='h-5 w-16 rounded-sm' /></div>
              <div className='px-3'><Skeleton className='h-4 w-16 rounded-sm' /></div>
              <div className='px-3'><Skeleton className='h-4 w-4 rounded-sm' /></div>
              <div className='px-3'><Skeleton className='h-5 w-14 rounded-sm' /></div>
              <div className='px-3'><Skeleton className='h-3 w-20 rounded-sm' /></div>
              <div className='px-1'><Skeleton className='size-6 rounded-sm' /></div>
            </div>
          ))}
        </div>
      </div>
    )
  if (query.isError) return <p role='alert'>{query.error.message}</p>
  return (
    <div className='space-y-5'>
      <p className='text-muted-foreground text-sm'>
        {required.filter((d) => d.preparation === 'prepared').length} of{' '}
        {required.length} required materials ready.
      </p>
      <div role='search' aria-label='Find key documents' className='flex flex-col gap-2 sm:flex-row sm:items-center'>
        <div className='relative w-full sm:w-80'>
          <Search className='text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2' aria-hidden='true' />
          <Input
            aria-label='Search key documents'
            placeholder='Search documents…'
            className='pl-9'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={filter} onValueChange={(value) => setFilter(value as typeof filter)}>
          <SelectTrigger aria-label='Filter by requirement' className='w-full text-[12px] sm:w-auto sm:min-w-40'>
            <span className='flex items-center gap-2'>
              <ListFilter className='text-muted-foreground size-4 shrink-0' aria-hidden='true' />
              <SelectValue />
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All ({docs.length})</SelectItem>
            <SelectItem value='required'>Required ({required.length})</SelectItem>
            <SelectItem value='optional'>Optional ({optional.length})</SelectItem>
          </SelectContent>
        </Select>
        <Select value={preparation} onValueChange={setPreparation}>
          <SelectTrigger aria-label='Filter by preparation' className='w-full text-[12px] sm:w-auto sm:min-w-40'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All preparation</SelectItem>
            {PREPARATION_LABELS.map((label) => (
              <SelectItem key={label} value={label}>
                {label} ({docs.filter((d) => preparationStatus(d).label === label).length})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger aria-label='Filter by status' className='w-full text-[12px] sm:w-auto sm:min-w-32'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All statuses</SelectItem>
            {Object.entries(VAULT_STATUS_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label} ({docs.filter((d) => hasStatus(d, value)).length})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={framework} onValueChange={setFramework}>
          <SelectTrigger aria-label='Filter by framework' className='w-full text-[12px] sm:w-auto sm:min-w-36'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All frameworks</SelectItem>
            {frameworks.map((value) => (
              <SelectItem key={value} value={value}>
                {value} ({docs.filter((d) => d.frameworks?.includes(value)).length})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {visible.length > 0 && (
        <div className='border-border min-w-0 overflow-x-auto rounded-sm border bg-card'>
          <table className='w-full min-w-[1040px]'>
            <thead className='sticky top-0 z-10'>
              <tr className='border-border bg-surface-subtle h-[40px] border-b'>
                <th className='font-body text-text-subtle px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                  Document name
                </th>
                <th className='font-body text-text-subtle w-[120px] px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                  Preparation
                </th>
                <th className='font-body text-text-subtle w-[110px] px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                  Category
                </th>
                <th className='font-body text-text-subtle w-[80px] px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                  Source
                </th>
                <th className='font-body text-text-subtle w-[90px] px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                  Status
                </th>
                <th className='font-body text-text-subtle w-[120px] px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                  Requirement
                </th>
                <th className='font-body text-text-subtle w-[130px] px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                  Related tests
                </th>
                <th className='w-[44px]'>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            {(['Governance', 'Security', 'Engineering', 'AI'] as const).map((category) => {
              const items = visible.filter((d) => d.category === category)
              if (!items.length) return null
              return (
                <tbody key={category} aria-label={`${category} documents`}>
                  <tr className='bg-surface-subtle'>
                    <th colSpan={8} scope='rowgroup' className='px-3 py-2 text-left'>
                      <h3 className='flex items-baseline gap-2'>
                        <span className='font-heading text-text-subtle text-[11px] font-semibold tracking-[0.8px] uppercase'>
                          {category}
                        </span>
                        <span className='text-text-disabled text-[11px] font-normal'>
                          {items.length}
                        </span>
                      </h3>
                    </th>
                  </tr>
                  {items.map((d) => {
                    const categories = [...new Set(d.documents.map((document) => document.category).filter(Boolean))]
                    const sources = [...new Set(d.documents.map((document) => document.sourceType).filter(Boolean))]
                    const statuses = [...new Set(d.documents.map((document) => document.status).filter(Boolean))]
                    const prep = preparationStatus(d)

                    return <tr
                      key={d.key}
                      className='border-border border-b transition-colors last:border-b-0 hover:bg-surface-subtle'
                    >
                      <td className='px-3 py-2.5'>
                        <div className='flex gap-2'>
                          <span className='flex h-5 shrink-0 items-center' title={prep.label} aria-hidden='true'>
                            <span data-testid='preparation-dot' className={cn(dotVariants({ status: prep.status }), 'size-2')} />
                          </span>
                          <div className='min-w-0'>
                            <div className='flex flex-wrap items-center gap-1.5'>
                              <Link
                                to={d.editorPath ? `/workspaces/${workspaceId}${d.editorPath}` : '/workspaces/$workspaceId/documents'}
                                params={{ workspaceId }}
                                search={d.editorPath
                                  ? d.editorPath === '/system-description'
                                    ? { from: 'documents' as const }
                                    : undefined
                                  : { tab: 'key', keyDocument: d.key }}
                                className='font-heading text-text-secondary text-[13px] hover:text-cobalt-strong hover:underline'
                              >
                                {d.title}
                              </Link>
                              {(d.frameworks?.length ?? 0) > 0 && (
                                <div className='flex flex-wrap items-center gap-1' aria-label={`Frameworks: ${d.frameworks?.join(', ')}`}>
                                  {d.frameworks?.map((framework) => (
                                    <Badge
                                      key={framework}
                                      variant='outline'
                                      className='border-ring/30 bg-primary-subtle text-cobalt-strong rounded-sm px-1.5 py-0.5 text-[9px] font-medium'
                                    >
                                      {framework}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <p className='font-body text-text-subtle mt-1 line-clamp-2 text-[11px] leading-snug'>
                              {d.guidance}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className='px-3 py-2.5'>
                        <Badge status={prep.status}>
                          {prep.label}
                        </Badge>
                      </td>
                      <td className='px-3 py-2.5'>
                        <div className='flex flex-wrap gap-1'>
                          {categories.length ? categories.map((category) => (
                            <VaultDocumentCategoryBadge key={category} category={category} />
                          )) : <span className='text-text-disabled text-[11px]'>—</span>}
                        </div>
                      </td>
                      <td className='px-3 py-2.5'>
                        <div className='flex items-center gap-1.5'>
                          {sources.length ? sources.map((source) => (
                            <VaultDocumentSourceIcon key={source} sourceType={source} />
                          )) : <span className='text-text-disabled text-[11px]'>—</span>}
                        </div>
                      </td>
                      <td className='px-3 py-2.5'>
                        <div className='flex flex-wrap gap-1'>
                          {statuses.length ? statuses.map((status) => (
                            <VaultDocumentStatusBadge key={status} status={status} />
                          )) : <span className='text-text-disabled text-[11px]'>—</span>}
                        </div>
                      </td>
                      <td className='px-3 py-2.5'>
                        <Badge
                          status={
                            d.requirement === 'required' ? 'amber' : 'default'
                          }
                        >
                          {d.requirement === 'required' ? 'Required' : 'Optional'}
                        </Badge>
                      </td>
                      <td className='font-body text-text-subtle px-3 py-2.5 text-[12px]'>
                        {d.tests.length} related test
                        {d.tests.length === 1 ? '' : 's'}
                      </td>
                      <td className='px-1 py-2.5'>
                        <Link
                          to={d.editorPath ? `/workspaces/${workspaceId}${d.editorPath}` : '/workspaces/$workspaceId/documents'}
                          params={{ workspaceId }}
                          search={d.editorPath
                            ? d.editorPath === '/system-description'
                              ? { from: 'documents' as const }
                              : undefined
                            : { tab: 'key', keyDocument: d.key }}
                          aria-label={`Edit ${d.title}`}
                          className='text-text-subtle hover:bg-muted hover:text-text-secondary inline-flex h-6 w-6 items-center justify-center rounded-sm'
                        >
                          <Pencil className='h-4 w-4' aria-hidden='true' />
                        </Link>
                      </td>
                    </tr>
                  })}
                </tbody>
              )
            })}
          </table>
        </div>
      )}
      {!visible.length && (
        <p className='text-muted-foreground p-8 text-center text-sm'>
          {search || preparation !== 'all' || status !== 'all' || framework !== 'all'
            ? 'No documents match your search.'
            : 'No required key documents in the current test scope.'}
        </p>
      )}
    </div>
  )
}

const ORG_CHART_KEY_DOCUMENT = 'org-chart'

/** File selection belongs beside the document, never in the Test status sidebar. */
export function KeyDocumentSources({
  entry,
  workspaceId,
  documentId,
  documentIds,
  testId,
  readOnly = false,
  editing = false,
  empty = false,
}: {
  entry: KeyDocumentRequirementDTO
  workspaceId: string
  documentId?: string
  documentIds?: string[]
  testId?: string
  readOnly?: boolean
  editing?: boolean
  empty?: boolean
}) {
  const navigate = useNavigate(),
    queryClient = useQueryClient(),
    askAi = useAskAi()
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)
  const [picker, setPicker] = useState(false),
    [upload, setUpload] = useState(false),
    [search, setSearch] = useState('')
  const materialIds = documentIds ?? (documentId ? [documentId] : [])
  const [selected, setSelected] = useState<string[]>(materialIds)
  const library = useQuery({
    ...trpc.vaultDocument.list.queryOptions({
      status: 'ACTIVE',
      pageSize: 200,
      search,
    }),
    enabled: picker,
  })
  const saveSelection = useMutation(
    trpc.vaultDocument.selectKeyDocumentMaterials.mutationOptions()
  )
  const handleUseFiles = async (documentIds: string[], focusId = documentIds[0]) => {
    try {
      const result = await saveSelection.mutateAsync({
        key: entry.key,
        documentIds,
      })
      setPicker(false)
      await open(focusId, result.documentIds)
    } finally {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: trpc.vaultDocument.keyDocuments.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.evidenceDocLink.list.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.vaultDocument.getById.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.cesTool.resolve.queryKey(),
        }),
      ])
    }
  }
  const chooseExisting = () => {
    setSelected(
      empty ? materialIds.filter((id) => id !== documentId) : materialIds
    )
    setPicker(true)
  }
  const open = (id: string, ids = materialIds.length ? materialIds : [id]) =>
    navigate({
      to: '/workspaces/$workspaceId/documents/$docId',
      params: { workspaceId, docId: id },
      search: { keyDocument: entry.key, documentIds: ids, ...(testId ? { testId } : {}) },
    })
  const showEmptyState = !documentId || empty
  const canPrepare = !readOnly && !editing && canWorkspaceWrite
  // #1995: the org chart is drawn from the People roster, so its key document
  // shows the chart itself instead of an empty draft.
  const reportTests = entry.tests.filter(
    (t) => t.coverage === 'full' && !['SUBMITTED', 'APPROVED'].includes(t.status)
  )
  const generateReport = useMutation(trpc.evidenceFile.generateReport.mutationOptions())
  const handleGenerateReport = async () => {
    try {
      await Promise.all(reportTests.map((t) => generateReport.mutateAsync({ statusId: t.statusId })))
      toast.success('Org chart PDF attached to the test as evidence')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not generate the report.')
    } finally {
      // Refresh what the test page's own Generate Report refreshes, plus its suggestions.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: trpc.vaultDocument.keyDocuments.queryKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.compliance.getControlsTree.queryKey() }),
        ...reportTests.flatMap(({ statusId }) => [
          queryClient.invalidateQueries({ queryKey: trpc.guidedTemplate.getStatusById.queryKey({ id: statusId }) }),
          queryClient.invalidateQueries({ queryKey: trpc.evidenceFile.list.queryKey({ statusId }) }),
          queryClient.invalidateQueries({ queryKey: trpc.cesTool.resolve.queryKey({ statusId }) }),
        ]),
      ])
    }
  }
  const orgChartView = showEmptyState && entry.key === ORG_CHART_KEY_DOCUMENT && (
      <div className='bg-muted flex min-h-full flex-col gap-4 px-8 py-6'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div className='min-w-0'>
            <h2 className='font-heading text-foreground text-[20px] leading-[1.25] font-semibold tracking-[-0.01em]'>
              {entry.title}
            </h2>
            <p className='text-muted-foreground mt-1 max-w-[640px] text-[13px] leading-5'>{entry.guidance}</p>
          </div>
          {canPrepare && (
            <div className='flex flex-wrap items-center gap-2'>
              <Button
                size='sm'
                className='h-auto px-3.5 py-2 text-[12px]'
                disabled={!reportTests.length || generateReport.isPending}
                title={reportTests.length ? undefined : 'The related test is already submitted.'}
                onClick={handleGenerateReport}
              >
                <Workflow className='size-3.5' />
                {generateReport.isPending ? 'Generating…' : 'Generate report'}
              </Button>
              <Button variant='outline' size='sm' className='h-auto px-3.5 py-2 text-[12px]' onClick={() => setUpload(true)}>
                <Upload className='size-3.5' />
                Upload document
              </Button>
              <Button variant='ghost' size='sm' className='h-auto px-3.5 py-2 text-[12px]' onClick={chooseExisting}>
                Use existing
              </Button>
            </div>
          )}
        </div>
        <OrgChart editable={!readOnly && canWorkspaceWrite} inKeyDocument />
      </div>
  )
  return (
    <div
      className={
        showEmptyState
          ? 'flex min-h-full flex-col gap-4'
          : 'bg-muted space-y-4 '
      }
    >
      {orgChartView || (showEmptyState && (
        <div className='bg-muted flex min-h-full flex-1 justify-center px-20 py-10'>
          <div className='border-border-divider flex min-h-[calc(var(--app-vh)_-_170px)] w-full max-w-[816px] items-center justify-center rounded-sm border border-dashed bg-card px-16 py-12'>
            <div className='mx-auto w-full max-w-[460px] text-center'>
              <FileText className='text-text-placeholder mx-auto size-10' />
              <h2 className='font-heading text-foreground mt-4 text-[20px] leading-[1.25] font-semibold tracking-[-0.01em]'>
                {entry.title}
              </h2>
              <p className='text-muted-foreground mx-auto mt-2 max-w-[400px] text-[13px] leading-5'>
                {entry.guidance}
              </p>
              {!readOnly && !editing && canWorkspaceWrite && (
                <div className='mt-5 flex flex-wrap items-center justify-center gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  className='h-auto px-3.5 py-2 text-[12px]'
                  onClick={() => setUpload(true)}
                >
                  <Upload className='size-3.5' />
                  Upload document
                </Button>
                {!entry.uploadOnly && (
                <Button
                  size='sm'
                  className='h-auto px-3.5 py-2 text-[12px]'
                  onClick={() =>
                    askAi({
                      text: `Draft our ${entry.title} using a structured template appropriate to this material. ${entry.guidance}\nCurrent requirements:\n${[
                        ...entry.tests,
                      ]
                        .sort(
                          (a, b) =>
                            Number(a.coverage === 'partial') -
                            Number(b.coverage === 'partial')
                        )
                        .map(
                          (t) =>
                            `${t.taskCode ? `TEST-${t.taskCode.replace(/^TEST-/, '')} · ` : ''}${t.title} [${t.status}]\n${t.coverage === 'full' ? 'Document requirement' : 'Supporting context only (this document does not complete this Test)'}: ${t.auditorAsk}\n${t.instructions ?? ''}${t.coverage === 'partial' ? `\nEvidence still required separately: ${t.remaining ?? 'Actual implementation or activity evidence.'}` : ''}`
                        )
                        .join(
                          '\n\n'
                        )}\nDraft only this document. Use supporting requirements to inform relevant procedures; do not generate completed exercises, notification records or other proof of activities. Use verified company facts. Mark missing facts for the user to complete; do not invent events, approvals or signatures. The user must be able to edit the draft and request revisions.\n${documentId ? `Update the existing blank draft ${documentId}. ` : ''}Save with keyDocumentKey=${entry.key}. Do not confirm materials or submit tests.`,
                      threadName: entry.title,
                      page: 'key-document',
                      pageSummary: entry.title,
                      threadScope: {
                        workspaceId,
                        scopeType: 'key-document',
                        scopeId: entry.key,
                        scopeLabel: entry.title,
                      },
                      pageData: {
                        keyDocumentKey: entry.key,
                        ...(documentId ? { documentId } : {}),
                      },
                    })
                  }
                >
                  <Sparkles className='size-3.5' />
                  Generate with Vera
                </Button>
                )}
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-auto px-3.5 py-2 text-[12px]'
                  onClick={chooseExisting}
                >
                  Use existing
                </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      <UploadDocumentDialog
        keyDocumentKey={entry.key}
        open={upload}
        onOpenChange={setUpload}
        onUploaded={async (ids) => {
          if (ids.length)
            await handleUseFiles(
              [
                ...new Set([
                  ...materialIds.filter((id) => !empty || id !== documentId),
                  ...ids,
                ]),
              ],
              ids[0]
            )
        }}
      />
      <Dialog open={picker} onOpenChange={setPicker}>
        <DialogContent className='grid-cols-1'>
          <DialogHeader>
            <DialogTitle>Use existing documents</DialogTitle>
            <DialogDescription>
              Use these files as evidence for the related Tests. This does not
              submit any Test for review.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='px-6'>
              <Input
                aria-label='Search existing documents'
                placeholder='Search files…'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <ScrollArea type='hover' className='max-h-72' viewClassName='max-h-72 [&>div]:!block'>
              <div className='space-y-2 px-6'>
                {library.isPending && (
                  <div role='status' aria-label='Loading files' className='space-y-2'>
                    {[0, 1, 2].map((row) => (
                      <div key={row} className='flex items-center gap-2 rounded-sm border p-3'>
                        <Skeleton className='size-4 shrink-0 rounded-sm' />
                        <Skeleton className='h-4 flex-1 rounded-sm' />
                      </div>
                    ))}
                  </div>
                )}
                {library.data?.items.map((d) => (
                  <label
                    className='flex items-start gap-2 rounded-sm border p-3 text-sm'
                    key={d.id}
                  >
                    <Checkbox
                      className='shrink-0'
                      disabled={saveSelection.isPending}
                      checked={selected.includes(d.id)}
                      onCheckedChange={() =>
                        setSelected(
                          selected.includes(d.id)
                            ? selected.filter((id) => id !== d.id)
                            : [...selected, d.id]
                        )
                      }
                    />
                    <span className='min-w-0 flex-1 break-words'>
                      {d.title}
                      {entry.documents.some(
                        (ref) => ref.id === d.id && ref.isLinked
                      ) && (
                        <small className='text-muted-foreground block'>
                          Linked to a related Test
                        </small>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </ScrollArea>
            {library.isError && <p role='alert' className='px-6'>{library.error.message}</p>}
            {library.data && library.data.total > library.data.items.length && (
              <p className='text-muted-foreground px-6 text-xs'>
                Search to narrow the first 200 matches.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              disabled={saveSelection.isPending}
              onClick={() => setPicker(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={!selected.length || saveSelection.isPending}
              onClick={async () => {
                try {
                  await handleUseFiles(selected)
                } catch (error) {
                  toast.error(
                    error instanceof Error
                      ? error.message
                      : 'Could not save the selected documents.'
                  )
                }
              }}
            >
              {saveSelection.isPending
                ? 'Saving…'
                : `Use ${selected.length || ''} file${selected.length === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** Related Tests are context; only the final submit dialog offers target selection. */
export function KeyDocumentPreparation({
  entry,
  workspaceId,
  documentId,
  documentIds,
  testId,
  editing = false,
  readOnly = false,
  headerActions = false,
  headerLeading,
  headerTrailing,
  showPreparationStatus = true,
  showActions = true,
}: {
  entry: KeyDocumentRequirementDTO
  workspaceId: string
  documentId?: string
  documentIds?: string[]
  testId?: string
  editing?: boolean
  readOnly?: boolean
  headerActions?: boolean
  headerLeading?: ReactNode
  headerTrailing?: ReactNode
  showPreparationStatus?: boolean
  showActions?: boolean
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)
  const ids = documentIds ?? (documentId ? [documentId] : [])
  const programQuery = useQuery(trpc.complianceProgram.getProgram.queryOptions())
  const continuous = !!programQuery.data && programQuery.data.evidenceWorkflowVersion >= 2
  const materials = useQueries({
    queries: ids.map((id) => trpc.vaultDocument.getById.queryOptions({ id })),
  })
  const [confirming, setConfirming] = useState(false)
  const [selectedTests, setSelectedTests] = useState<string[]>([])
  const [results, setResults] = useState<
    Array<{ statusId: string; outcome: string; reason?: string }>
  >([])
  const complete = useMutation(
    trpc.vaultDocument.completeAndLink.mutationOptions()
  )
  const submit = useMutation(
    trpc.vaultDocument.submitKeyDocument.mutationOptions()
  )
  const editableTests = entry.tests.filter(
    (t) => !['APPROVED', 'PASSED', 'SUBMITTED'].includes(t.status)
  )
  const full = editableTests.filter(
    (t) => t.coverage === 'full' && t.eligibility
  )
  const revision =
    ids.join(':') +
    materials.map((q) => q.data?.contentHash).join(':') +
    entry.tests.map((t) => t.fingerprint).join(':')
  useEffect(() => {
    setConfirming(false)
  }, [revision, editing])
  const busy = complete.isPending || submit.isPending
  const refresh = async () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.vaultDocument.keyDocuments.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.vaultDocument.getById.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.vaultDocument.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.vaultDocument.versions.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.evidenceDocLink.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.cesTool.resolve.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getStatusById.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getGuidedPath.queryKey(),
      }),
    ])
  const confirm = async () => {
    try {
      const documents = materials.map((q) => {
        if (!q.data?.contentHash) throw new Error('Wait for the files to load.')
        return { id: q.data.id, expectedContentHash: q.data.contentHash }
      })
      const targets = [
        ...new Set([
          ...selectedTests,
          ...editableTests
            .filter((t) => t.coverage === 'partial')
            .map((t) => t.statusId),
        ]),
      ]
      const result = await complete.mutateAsync({
        key: entry.key,
        documents,
        selectedStatusIds: targets,
        expectedTestFingerprints: Object.fromEntries(
          entry.tests
            .filter((t) => targets.includes(t.statusId))
            .map((t) => [t.statusId, t.fingerprint])
        ),
        confirmed: true,
        forSubmission: true,
      })
      if (!result.submissionPreview)
        throw new Error(
          'Materials are saved, but submission needs another review. No Test was submitted.'
        )
      const submitted = await submit.mutateAsync({
        key: entry.key,
        documentIds: ids,
        fingerprint: result.submissionPreview.fingerprint,
        selectedStatusIds: selectedTests,
      })
      setResults(submitted.results)
      const failures = submitted.results.filter((result) => result.outcome === 'failed')
      if (failures.length) {
        toast.error(failures.map((result) => result.reason ?? 'Submission failed').join('\n'))
      } else {
        toast.success(`${submitted.results.length} Test${submitted.results.length === 1 ? '' : 's'} submitted${continuous ? ' for Control review' : ' for review'}`)
      }
      setConfirming(false)
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'Could not complete this action'
      )
    } finally {
      await refresh()
    }
  }
  const documentSwitcher =
    documentId && ids.length > 1 ? (
      <Select
        value={ids.includes(documentId) ? documentId : undefined}
        onValueChange={(id) =>
          navigate({
            to: '/workspaces/$workspaceId/documents/$docId',
            params: { workspaceId, docId: id },
            search: { keyDocument: entry.key, documentIds: ids, ...(testId ? { testId } : {}) },
          })
        }
        disabled={editing}
      >
        <SelectTrigger
          className='h-8 w-40 min-w-0 text-[12px] sm:w-60 [&>span]:truncate'
          aria-label='View material document'
          title={materials[ids.indexOf(documentId)]?.data?.title}
        >
          <SelectValue placeholder='View document' />
        </SelectTrigger>
        <SelectContent>
          {ids.map((id, index) => (
            <SelectItem key={id} value={id}>
              {materials[index]?.data?.title ?? 'Loading document…'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : null
  const action =
    !readOnly && canWorkspaceWrite && full.length > 0 && ids.length > 0 ? (
      <Button
        size='sm'
        disabled={
          busy || editing || !programQuery.data || materials.some((q) => !q.data || q.isFetching)
        }
        onClick={() => {
          setSelectedTests(full.map((t) => t.statusId))
          setConfirming(true)
        }}
      >
        {continuous ? 'Submit evidence' : 'Submit for review'}
      </Button>
    ) : null
  return (
    <div
      className='w-full space-y-4 text-sm'
      data-testid='key-document-preparation'
    >
      {programQuery.error && <p role='alert' className='text-sm text-destructive'>{programQuery.error.message}</p>}
      {showActions &&
        (headerActions ? (
          <PageHeaderActions>
            {headerLeading}
            {documentSwitcher}
            {action}
            {headerTrailing}
          </PageHeaderActions>
        ) : (
          <>
            {documentSwitcher}
            {action}
          </>
        ))}
      <div className='flex items-center gap-2'>
        {showPreparationStatus && (
          <KeyDocumentPreparationBadge entry={entry} />
        )}
        <span className='text-muted-foreground text-xs'>
          {entry.requirement === 'required'
            ? 'Required material'
            : 'Optional material'}
        </span>
      </div>
      {!full.length && editableTests.some((test) => test.coverage === 'partial') && (
        <p className='text-muted-foreground text-xs'>
          This document provides part of the evidence. After saving it, open a
          related Test below to add the remaining evidence and submit for review.
        </p>
      )}
      <div className='space-y-2'>
        {entry.tests.map((t) => (
          <DocumentRelatedTest
            key={t.statusId}
            test={t}
            workspaceId={workspaceId}
            showStatus={false}
          >
            {t.coverage === 'partial' && full.length > 0 && (
              <p className='text-muted-foreground text-xs'>
                Open this Test to add the remaining evidence and submit it for review.
              </p>
            )}
            <details>
              <summary className='text-muted-foreground cursor-pointer text-xs'>
                Requirement details
              </summary>
              <div className='text-muted-foreground mt-2 space-y-2 text-xs'>
                <p className='inline-flex items-center gap-1.5'>
                  Reviewer:
                  {t.reviewer && (
                    <MemberAvatar person={t.reviewer} size='xs' />
                  )}
                  {t.reviewer?.name ?? 'Unassigned'}
                </p>
                <p className='whitespace-pre-wrap'>{t.auditorAsk}</p>
                <p className='whitespace-pre-wrap'>{t.instructions}</p>
                {t.remaining && <p>{t.remaining}</p>}
                {t.reasons.map((reason) => <p key={reason}>{reason}</p>)}
              </div>
            </details>
          </DocumentRelatedTest>
        ))}
      </div>
      {!editableTests.length && (
        <p className='text-muted-foreground text-xs'>
          These Tests are already submitted or approved. Their existing evidence
          is preserved.
        </p>
      )}
      {editing && (
        <p className='text-muted-foreground text-xs'>
          Save your changes before continuing.
        </p>
      )}
      {!!results.length && (
        <div role='status' className='space-y-2 text-xs'>
          {results.map((r, i) => (
            <p key={i}>
              {entry.tests.find((t) => t.statusId === r.statusId)?.title}:{' '}
              {r.outcome}
              {r.reason ? ` · ${r.reason}` : ''}
            </p>
          ))}
        </div>
      )}
      <Dialog
        open={confirming}
        onOpenChange={(open) => {
          if (!open && !busy) setConfirming(false)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{continuous ? 'Submit evidence' : 'Submit for review'}</DialogTitle>
            <DialogDescription>
              {continuous
                ? 'Submit these documents and all current evidence attached to each selected Test for Control-level review. This does not start a Test approval.'
                : 'Confirm these files are accurate. Only the selected Tests below will be sent to their reviewers with these documents and all current evidence attached to each Test.'}
              {' '}Unavailable files must be restored or removed from the Test before submitting.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 px-6'>
            <div className='space-y-1 text-sm'>
              {materials.map((q, i) => (
                <p key={ids[i]} className='break-words'>
                  {q.data?.title}
                </p>
              ))}
            </div>
            <div className='space-y-2'>
              {full.map((t) => (
                <label
                  className='flex items-start gap-2 rounded-sm border p-3 text-sm'
                  key={t.statusId}
                >
                  <Checkbox
                    checked={selectedTests.includes(t.statusId)}
                    disabled={busy}
                    onCheckedChange={() =>
                      setSelectedTests(
                        selectedTests.includes(t.statusId)
                          ? selectedTests.filter((id) => id !== t.statusId)
                          : [...selectedTests, t.statusId]
                      )
                    }
                  />
                  <span>
                    {t.title}
                    {!continuous && <small className='text-muted-foreground block'>
                      Reviewer: {t.reviewer?.name ?? 'Unassigned'}
                    </small>}
                  </span>
                </label>
              ))}
            </div>
            {editableTests
              .filter((t) => !full.includes(t))
              .map((t) => (
                <p key={t.statusId} className='text-muted-foreground text-xs'>
                  <span className='block font-medium'>
                    {t.taskCode ? `${t.taskCode} · ` : ''}
                    {t.title} · not submitted
                  </span>
                  {t.remaining ?? t.reasons.join(' ')}
                </p>
              ))}
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy || !selectedTests.length} onClick={confirm}>
              {busy
                ? 'Processing…'
                : `Submit ${selectedTests.length} Test${selectedTests.length === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
